import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import * as actions from '../../../../../store';
import { Card, Zoom, Tooltip, Menu, MenuItem, Typography, Divider } from '@material-ui/core';
import LineComponent from '../LineComponent/LineComponent';
import { API_ENDPOINT } from '../../../../../config';
import ContourOverlay from '../ContourOverlay/ContourOverlay';
import animation from '../../../../../resources/processing_animation.gif';
import DiagnoseOverlays from '../BoxesOverlay/DiagnoseOverlays';
import { fetchResults } from '../../../../../store';
import CachedIcon from '@material-ui/icons/Cached';
import { generatePectoralisShape, drawPectoralisShapeOnCanvas } from './helpers';
import { parseBreastBox } from '../../../../../helpers/breastBox';

import missing_rcc from '../../../../../resources/missing_rcc.png';
import missing_lcc from '../../../../../resources/missing_lcc.png';
import missing_rmlo from '../../../../../resources/missing_rmlo.png';
import missing_lmlo from '../../../../../resources/missing_lmlo.png';

const useStyles = makeStyles((theme) => ({
  projection_container: {
    display: 'flex',
    position: 'relative',
    maxHeight: '100%',
  },

  cropViewport: {
    position: 'relative',
    overflow: 'hidden',
    width: 'auto',
    maxWidth: '100%',
    minWidth: 0,
    minHeight: 0,
    borderRadius: '5px',
  },

  cropInner: {
    position: 'absolute',
  },

  projection_image: {
    position: 'relative',
    width: '100%',
    borderRadius: '5px',
    height: '100%',
    zIndex: 1,
    objectFit: 'contain',
  },

  projection_image_v: {
    position: 'relative',
    maxWidth: '100%',
    borderRadius: '5px',
    zIndex: 1,
  },

  animation: {
    width: '50%',
    height: 'auto',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: '10',
    opacity: 0.6,
  },

  evaluate_icon_container: {
    color: theme.palette.primary.main,
    top: 0,
    left: 0,
    color: '#f44336',
    width: '100%',
    height: '100%',
    display: 'flex',
    opacity: '80%',
    zIndex: 10,
    position: 'absolute',
    alignItems: 'flex-start',
    justifyContent: 'center',
    cursor: 'pointer',
  },

  tooltip_text: {
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    fontSize: '12px',
  },

  not_eval_text: {
    width: '90%',
    letterSpacing: '0.1rem',
    fontSize: theme.fonts.responsiveSmall,
    background: 'black',
    margin: '20px 6px',
    textAlign: 'center',
  },

  menu_item_text: {
    fontSize: theme.fonts.responsiveMedium,
    letterSpacing: '0.1rem',
  },

  evaluate_icon: {
    color: theme.palette.primary.main,
    width: '35%',
    height: 'auto',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: '10',
    opacity: '30%',
    cursor: 'pointer',
  },
}));

export default function Projection(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const canvasOverlaysRef = useRef(null);
  const canvasBoxesRef = useRef(null);
  const canvasOffSetX = useRef(null);
  const canvasOffSetY = useRef(null);

  // Store state
  const {
    drawing,
    qualityInRevisionMode,
    shownQualityOverlayType,
    qualityInDrawingMode,
    diagnosticsInRevisionMode,
    densityInRevisionMode,
  } = useSelector((state) => state.labeling);
  const results = useSelector((state) => state.results);
  const { projections } = useSelector((state) => state.results);
  const { currentExam, currentlyProcessed } = useSelector((state) => state.examinations);
  const {
    qualityOverlaysToShow,
    parenchymaCutsToShow,
    linesToShow,
    pectAngleLinesToShow,
    nippleProfileToShow,
    IMFToShow,
    pectLevelToShow,
    projectionsSalt,
  } = useSelector((state) => state.images);
  const { tabsIndex, landscape, zoomedProjection } = useSelector((state) => state.gui);
  const { premium_features, user_settings } = useSelector((state) => state.global_settings);
  const cropToBreast = !!user_settings?.crop_to_breast;

  // Local state
  const [imgSize, setImgSize] = useState([0, 0]);
  const [leftDown, setLeftDown] = useState(false);
  const [middleDown, setMiddleDown] = useState(false);
  const [imgPosition, setImgPosition] = useState([0, 0]);
  const [zoom, setZoom] = useState(1);
  const [movePoint, setMovePoint] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [src, setSrc] = useState('');
  const [tempRectangle, setTempRectangle] = useState(null);
  const [projContextMenu, setProjContextMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [currentValues, setCurrentValues] = useState(null);

  // Variables;
  const { diagnostics_microcalc_module, diagnostics_opacities_module, data_annotation } = premium_features;

  const somePremiumDiagnostics =
    (diagnostics_microcalc_module || diagnostics_opacities_module) &&
    localStorage.getItem('diagnostics') === 'true';
  const annotatorRole = data_annotation && localStorage.getItem('annotation') === 'true';

  const pectoLevelLineToShow = Object.keys(pectLevelToShow).filter((key) => pectLevelToShow[key] === true);
  const showPnlLines = Object.values(linesToShow).some((value) => value === true);
  const pnlLineToShow = Object.keys(linesToShow).filter((key) => linesToShow[key] === true);
  const showPectAngleLines = Object.values(pectAngleLinesToShow).some((value) => value === true);
  const IMFShow = Object.keys(IMFToShow).filter((key) => IMFToShow[key] === true);
  const pectAngleLineToShow = Object.keys(pectAngleLinesToShow).filter(
    (key) => pectAngleLinesToShow[key] === true,
  );
  const nippleEdgeToShow = Object.keys(nippleProfileToShow).filter(
    (key) => nippleProfileToShow[key] === true,
  );
  const parenchymaShow = Object.values(parenchymaCutsToShow).some(
    (value) => value === true || qualityOverlaysToShow.parenchymaRMLO || qualityOverlaysToShow.parenchymaLMLO,
  );
  const parenchymaCCShow = Object.values(parenchymaCutsToShow).some(
    (value) => value === true || qualityOverlaysToShow.parenchymaRCC || qualityOverlaysToShow.parenchymaLCC,
  );
  const parenchymaLateralShow = Object.values(parenchymaCutsToShow).some(
    (value) =>
      value === true ||
      qualityOverlaysToShow.parenchymaRCClateral ||
      qualityOverlaysToShow.parenchymaLCClateral,
  );
  const parenchymaMedialShow = Object.values(parenchymaCutsToShow).some(
    (value) =>
      value === true ||
      qualityOverlaysToShow.parenchymaRCCmedial ||
      qualityOverlaysToShow.parenchymaLCCmedial,
  );

  // useEffects
  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    dispatch(actions.setInitialRectangles());
    setTempRectangle(null);
    if (context) {
      context.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [diagnosticsInRevisionMode]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');

    if (context) {
      const canvasOffSet = canvas?.getBoundingClientRect();
      if (canvasOffSetX.current) {
        canvasOffSetX.current = canvasOffSet.left;
        canvasOffSetY.current = canvasOffSet.top;
      }

      const boundingRect = canvas?.getBoundingClientRect();
      if (boundingRect) {
        canvas.width = boundingRect.width;
        canvas.height = boundingRect.height;
        context.lineWidth = 3;
      }
    }

    if (isDrawing && tempRectangle) {
      context.strokeRect(
        tempRectangle.startX,
        tempRectangle.startY,
        tempRectangle.width,
        tempRectangle.height,
        tempRectangle.color,
        tempRectangle.rectangle_class,
        tempRectangle.rectangle_type,
      );
    }
  }, [props.microcalcRectangles, props.opacitiesRectangles, tempRectangle, zoomedProjection]);

  useEffect(() => {
    setZoom(1);
    setImgPosition([0, 0]);
  }, [zoomedProjection]);

  useEffect(() => {
    setZoom(1);
    setImgPosition([0, 0]);
    setImgSize([0, 0]);
  }, [currentExam, props?.src, props?.proj_name, cropToBreast]);

  useEffect(() => {
    const newSrc = !!props?.uid
      ? `${API_ENDPOINT}/static_images/${results.folder}/${props?.uid}.png?salt=${projectionsSalt}`
      : `${API_ENDPOINT}/static_images/${results.folder}/${
          projections[props.proj_name]?.uid
        }.png?salt=${projectionsSalt}`;
    setSrc(newSrc);

    dispatch(actions.setInitialRectangles());
    setTempRectangle(null);
  }, [props?.uid, results.folder, projectionsSalt]);

  useEffect(() => {
    const newCurrentValues = { ...props.qualityEvaluation };
    // if (!newCurrentValues.hasOwnProperty('location_nipple_projection')) {
    //   newCurrentValues['location_nipple_projection'] = [
    //     props.proj_name?.charAt(0) === 'r' ? 1 : 0,
    //     newCurrentValues?.location_nipple?.[1],
    //   ];
    // }
    setCurrentValues(newCurrentValues);
  }, [props.qualityEvaluation]);

  // Free drawing Handlers
  const onMouseDown = useCallback((event) => {
    if (event.button === 0) setLeftDown(true);
    else if (event.button === 1) setMiddleDown(true);
  }, []);

  const onMouseUp = useCallback(
    (event) => {
      if (event.button === 0) {
        setLeftDown(false);
        if (isDrawing) onDrawingFinished();
      } else if (event.button === 1) setMiddleDown(false);

      if (!!movePoint) {
        if (movePoint === 'location_pecto_bottom' || movePoint === 'location_pecto_top') {
          updatePectoShape(event.clientX, event.clientY);
        }
        setMovePoint(null);
      }
    },
    [movePoint, isDrawing],
  );

  function onMouseMoving({ clientX, clientY, movementX, movementY }) {
    let rect;
    if (tabsIndex === 1 || qualityInRevisionMode) {
      rect = canvasOverlaysRef.current?.getBoundingClientRect();
    } else {
      rect = canvasBoxesRef.current?.getBoundingClientRect();
    }

    if (!rect) return;

    if (!!movePoint) {
      let x = (clientX - rect.left) / rect.width;
      let y = (clientY - rect.top) / rect.height;
      if (movePoint == 'location_pecto_bottom') {
        if (props.proj_name.charAt(0) === 'r') {
          x = 1;
        } else {
          x = 0;
        }
      } else if (movePoint == 'location_pecto_top') {
        y = 0;
      }
      dispatch(actions.updateCustomQualityFeature(props?.proj_name.toUpperCase(), movePoint, [x, y], true));
    } else if (middleDown) {
      const inFrameCoords = makeSureInFrame(
        rect,
        movementX / 2 + imgPosition[0],
        movementY / 2 + imgPosition[1],
      );
      setImgPosition(inFrameCoords);
    } else if (leftDown) {
      const scaleX = imgSize[0] / rect.width;
      const scaleY = imgSize[1] / rect.height;
      const x = (clientX - rect.left) * scaleX;
      const y = (clientY - rect.top) * scaleY;
      draw(x, y, scaleX);
    }
  }

  function makeSureInFrame(rect, x, y) {
    const zoomFactor = zoomedProjection === props?.proj_name ? 2.02 * zoom : zoom;

    const limitX = (0.5 * (zoom - 1) * rect.width) / zoomFactor;
    const limitY = (0.5 * (zoom - 1) * rect.height) / zoomFactor;

    x = Math.min(x, limitX);
    y = Math.min(y, limitY);
    x = Math.max(x, -limitX);
    y = Math.max(y, -limitY);

    return [x, y];
  }

  function onCanvasClick({ clientX, clientY, button }) {
    if (button === 0) {
      const rect = canvasOverlaysRef.current.getBoundingClientRect();
      const scaleX = imgSize[0] / rect.width;
      const scaleY = imgSize[1] / rect.height;
      const x = (clientX - rect.left) * scaleX;
      const y = (clientY - rect.top) * scaleY;
      draw(x, y, scaleX);
    }
  }

  function draw(x, y, scale) {
    if (
      (qualityInRevisionMode && !qualityInDrawingMode) ||
      (qualityInRevisionMode && qualityInDrawingMode === '')
    )
      return;

    let color;
    const d = 0.3 * drawing.radius * scale;

    if (qualityOverlaysToShow.clipsRCC) color = theme.palette.qualityClasses.clips;
    else if (qualityOverlaysToShow.parenchymaRCC) color = theme.palette.qualityClasses.parenchyma;
    else if (qualityOverlaysToShow.skinFoldsRCC) color = theme.palette.qualityClasses.skinFolds;
    else if (qualityOverlaysToShow.pectoralisRCC) color = theme.palette.qualityClasses.pectoralis;
    else if (qualityOverlaysToShow.scarsRCC) color = theme.palette.qualityClasses.scars;
    else return;

    const context = canvasOverlaysRef.current.getContext('2d');

    if (drawing.mode === 'erase') context.globalCompositeOperation = 'destination-out';
    else context.globalCompositeOperation = 'source-over';

    context.fillStyle = color;
    context.beginPath();
    context.arc(x, y, d, 0, 2 * Math.PI);
    context.fill();

    context.globalCompositeOperation = 'source-over';
    setIsDrawing(true);
  }

  function onDrawingFinished() {
    setIsDrawing(false);
    const canvas = canvasOverlaysRef.current;
    const imageDataUrl = canvas.toDataURL('image/png');
    if (qualityInRevisionMode)
      dispatch(actions.updateCustomQualityOverlays(shownQualityOverlayType, props?.proj_name, imageDataUrl));

    dispatch(actions.setQualityLocalChanges(true));
  }

  function onMouseScroll(event) {
    const newValue = Math.max(-0.001 * event.deltaY + zoom, 1);
    if (newValue === 1) setImgPosition([0, 0]);
    setZoom(newValue);

    onMouseMoving(event);
  }

  // Arrow navigation handlers
  const moveImage = useCallback(
    (direction) => {
      if (zoom <= 1) return; // Only allow movement when zoomed

      const moveStep = 20; // Pixels to move per click
      let newX = imgPosition[0];
      let newY = imgPosition[1];

      switch (direction) {
        case 'up':
          newY = Math.max(imgPosition[1] - moveStep, -getMaxMovement().y);
          break;
        case 'down':
          newY = Math.min(imgPosition[1] + moveStep, getMaxMovement().y);
          break;
        case 'left':
          newX = Math.max(imgPosition[0] - moveStep, -getMaxMovement().x);
          break;
        case 'right':
          newX = Math.min(imgPosition[0] + moveStep, getMaxMovement().x);
          break;
        default:
          return;
      }

      setImgPosition([newX, newY]);
    },
    [zoom, imgPosition],
  );

  const getMaxMovement = useCallback(() => {
    const zoomFactor = zoomedProjection === props?.proj_name ? 2.02 * zoom : zoom;
    const rect =
      canvasOverlaysRef.current?.getBoundingClientRect() || canvasBoxesRef.current?.getBoundingClientRect();

    if (!rect) return { x: 0, y: 0 };

    const limitX = (0.5 * (zoom - 1) * rect.width) / zoomFactor;
    const limitY = (0.5 * (zoom - 1) * rect.height) / zoomFactor;

    return { x: limitX, y: limitY };
  }, [zoom, zoomedProjection, props?.proj_name]);

  // Expose moveImage function to parent component
  useEffect(() => {
    if (props.onMoveImage) {
      props.onMoveImage(moveImage, zoom);
    }
  }, [moveImage, zoom, props]);

  const enterEditMode = (module) => {
    if (module === 'quality') {
      dispatch(actions.setTabsIndex(2));
      dispatch(actions.setQualityInRevisionMode(true));
    } else if (module === 'diagnostics') {
      dispatch(actions.setTabsIndex(2));
      dispatch(actions.setDiagnosticsInRevisionMode(true));
    }

    setProjContextMenu(false);
  };

  const evaluateImage = () => {
    props.setEvaluated(true);
    dispatch(fetchResults());
  };

  const projectionClickHandler = (e, projName, projEval) => {
    if (densityInRevisionMode) {
      zoomedProjection === projName
        ? dispatch(actions.setZoomedProjection(null))
        : dispatch(actions.setZoomedProjection(projName));
    }

    if (projContextMenu) return;

    if (projEval) {
      (tabsIndex === 0 && !diagnosticsInRevisionMode) || tabsIndex === 1
        ? zoomedProjection === projName
          ? dispatch(actions.setZoomedProjection(null))
          : dispatch(actions.setZoomedProjection(projName))
        : setProjContextMenu(false);
    } else {
      e.preventDefault();
      evaluateImage();
    }
  };

  const projectionCursor = (projName, projEval) => {
    if (!projEval) return 'pointer';

    if (tabsIndex === 0 || tabsIndex === 1) {
      return zoomedProjection === projName ? 'zoom-out' : 'zoom-in';
    }

    if (tabsIndex === 2) {
      if (densityInRevisionMode) {
        return zoomedProjection === projName ? 'zoom-out' : 'zoom-in';
      }

      if (qualityInRevisionMode) {
        if (qualityInDrawingMode) {
          if (drawing.mode === 'draw') {
            return 'crosshair';
          }
          if (drawing.mode === 'erase') {
            return 'cell';
          }
        } else {
          return 'default';
        }
      }

      return 'default';
    }

    return 'default';
  };

  function getMissingProj() {
    if (props?.proj_name === 'rcc') return missing_rcc;
    if (props?.proj_name === 'lcc') return missing_lcc;
    if (props?.proj_name === 'rmlo') return missing_rmlo;
    if (props?.proj_name === 'lmlo') return missing_lmlo;
    return null;
  }

  function updatePectoShape(mouseX, mouseY) {
    // Calculate coordinates
    const rect = canvasOverlaysRef.current.getBoundingClientRect();
    let x = (mouseX - rect.left) / rect.width;
    let y = (mouseY - rect.top) / rect.height;
    let new_pecto_top = currentValues?.location_pecto_top;
    let new_pecto_bottom = currentValues?.location_pecto_bottom;
    if (movePoint == 'location_pecto_bottom') {
      if (props.proj_name.charAt(0) === 'r') {
        x = 1;
      } else {
        x = 0;
      }
      new_pecto_bottom = [x, y];
    } else if (movePoint == 'location_pecto_top') {
      y = 0;
      new_pecto_top = [x, y];
    }

    // Generate shape points
    const shapePoints = generatePectoralisShape(
      new_pecto_top,
      new_pecto_bottom,
      imgSize,
      props.proj_name.charAt(0) === 'r',
    );

    // Draw shape on canvas
    const imageDataUrl = drawPectoralisShapeOnCanvas(
      canvasOverlaysRef,
      shapePoints,
      theme.palette.qualityClasses.pectoralis,
    );

    // Update custom quality overlays
    dispatch(actions.updateCustomQualityOverlays('pectoralis', props.proj_name, imageDataUrl));
  }

  const breastBox = cropToBreast ? parseBreastBox(props.qualityEvaluation?.breast_box) : null;
  const cropActive = !!(breastBox && imgSize[0] > 0 && imgSize[1] > 0);
  const maxProjHeight = landscape ? '43vh' : 'calc(29vh)';

  const projection = (
    <div
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      className={classes.projection_container}
      onContextMenu={(event) => {
        event.preventDefault();
        if (
          (tabsIndex === 0 && props?.qualityEvaluation) ||
          tabsIndex === 1 ||
          (tabsIndex === 2 && qualityInRevisionMode)
        ) {
          setMenuPosition({ top: event.clientY, left: event.clientX });
          setProjContextMenu((prev) => !prev);
        }
      }}
      onClick={(e) => projectionClickHandler(e, props?.proj_name, props?.qualityEvaluation)}
      onDoubleClick={() =>
        qualityInRevisionMode
          ? zoomedProjection === props?.proj_name
            ? dispatch(actions.setZoomedProjection(null))
            : dispatch(actions.setZoomedProjection(props?.proj_name))
          : null
      }
      style={{
        transform: zoom > 1 ? `scale(${zoom})` : undefined,
        transformOrigin: cropActive
          ? `${(breastBox.x + breastBox.w / 2) * 100}% ${(breastBox.y + breastBox.h / 2) * 100}%`
          : undefined,
        top: imgPosition[1],
        left: imgPosition[0],
        width: cropActive ? '100%' : undefined,
        height: cropActive ? '100%' : undefined,
        justifyContent: props?.left ? 'flex-start' : 'flex-end',
        cursor: projectionCursor(props?.proj_name, props?.qualityEvaluation),
      }}>
      {/* Right click menu (to access revision) */}
      {projContextMenu && annotatorRole && tabsIndex === 0 && (
        <Menu
          open={projContextMenu}
          onClose={() => setProjContextMenu(false)}
          anchorReference='anchorPosition'
          anchorPosition={{ top: menuPosition.top, left: menuPosition.left }}
          sx={{
            '& .MuiPopover-paper': {
              position: 'fixed',
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
            },
          }}>
          <MenuItem key='quality' onClick={() => enterEditMode('quality')}>
            <Typography className={classes.menu_item_text}>
              {t('projections.revise', { module: 'b-QUALITY' })}
            </Typography>
          </MenuItem>

          {somePremiumDiagnostics && (
            <MenuItem key='diagnostics' onClick={() => enterEditMode('diagnostics')}>
              <Typography className={classes.menu_item_text}>
                {t('projections.revise', { module: 'b-DIAGNOSTICS' })}
              </Typography>
            </MenuItem>
          )}
        </Menu>
      )}
      {/* Overlays for Quality Revision */}
      {(tabsIndex === 0 || tabsIndex === 1 || (tabsIndex === 2 && qualityInRevisionMode)) && (
        <ContourOverlay
          proj_name={props?.proj_name}
          canvasRef={canvasOverlaysRef}
          imgSize={imgSize}
          onMouseDown={onCanvasClick}
          onMouseMove={onMouseMoving}
          onMouseUp={onMouseUp}
          onWheel={onMouseScroll}
          tabsIndex={tabsIndex}
          evaluation={props?.qualityEvaluation}
          parenchymaCutsToShow={parenchymaCutsToShow}
        />
      )}

      {/* Diagnostic Overlays and Revision Canvas */}
      {(tabsIndex === 0 || (tabsIndex === 2 && !qualityInRevisionMode)) && (
        <DiagnoseOverlays
          proj_name={props?.proj_name}
          canvasRef={canvasBoxesRef}
          imgSize={imgSize}
          onWheel={onMouseScroll}
          canvasOffSetX={canvasOffSetX}
          canvasOffSetY={canvasOffSetY}
          onMouseMove={onMouseMoving}
        />
      )}

      {/* Not evaluated projection */}
      {!props?.qualityEvaluation &&
        !props?.diagnosticsEvaluation &&
        !props?.fetchingEval &&
        !currentlyProcessed && (
          <Tooltip
            title={
              <>
                <Typography className={classes.tooltip_text}>{t('ResultView.not_evaluated')}</Typography>
                <Divider />
                <Typography className={classes.tooltip_text}>
                  {`${t('ResultView.click_here')} ${t('ResultView.eval')}`}
                </Typography>
              </>
            }
            arrow
            interactive
            placement='right-end'
            TransitionComponent={Zoom}>
            <span>
              <div className={classes.evaluate_icon_container}>
                <Typography className={classes.not_eval_text}>
                  {t('ResultView.not_evaluated_proj')}
                </Typography>
                <CachedIcon className={classes.evaluate_icon} />
              </div>
            </span>
          </Tooltip>
        )}

      {/* Reprocessing animation */}
      {!!props?.fetchingEval && (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'progress',
          }}>
          <img className={classes.animation} src={animation} alt='evaluating' />
        </div>
      )}

      {/* Projection image */}
      <img
        crossOrigin='use-credentials'
        className={landscape ? classes.projection_image : classes.projection_image_v}
        style={{
          maxWidth: '100%',
          maxHeight: cropActive ? '100%' : maxProjHeight,
          width: cropActive ? '100%' : undefined,
          height: cropActive ? '100%' : undefined,
          filter: props.invertedColor?.[props.proj_name] ? 'invert(1)' : undefined,
          objectFit: 'contain',
        }}
        src={src}
        alt={`NO ${props?.proj_name} ViewPosition`}
        onLoad={(event) => {
          if (props?.onLoad) props?.onLoad();
          setImgSize([event.target.naturalWidth, event.target.naturalHeight]);
        }}
        onError={(event) => {
          setSrc(getMissingProj());
        }}
        key={`${props.proj_name}-${props.invertedColor?.[props.proj_name]}`}
      />

      {/* IMF box  and PNL over projections*/}
      {(qualityInRevisionMode ||
        props?.proj_name === pnlLineToShow.toString() ||
        props?.proj_name === pectAngleLineToShow.toString() ||
        props?.proj_name === nippleEdgeToShow.toString() ||
        props?.proj_name === pectoLevelLineToShow.toString() ||
        props?.proj_name === IMFShow.toString()) && (
        <LineComponent
          onMouseUp={showPnlLines ? () => {} : onMouseUp}
          onMouseMove={showPnlLines ? () => {} : onMouseMoving}
          onWheel={showPnlLines ? () => {} : onMouseScroll}
          onMouseDown={showPnlLines ? () => {} : onCanvasClick}
          showRevisionLines={qualityInRevisionMode && !qualityInDrawingMode}
          projection={props?.projection}
          showProjLines={showPnlLines ? props?.proj_name === pnlLineToShow.toString() : false}
          proj_name={props?.proj_name}
          parenchymaShow={parenchymaShow || parenchymaCCShow}
          parenchymaMedialShow={parenchymaMedialShow}
          parenchymaLateralShow={parenchymaLateralShow}
          movePoint={movePoint}
          setMovePoint={setMovePoint}
          showPetcAngleLine={showPectAngleLines ? props?.proj_name === pectAngleLineToShow.toString() : false}
          showNippleEdge={props?.proj_name === nippleEdgeToShow.toString()}
          showIMF={props?.proj_name === IMFShow.toString()}
          showPectoLevel={props?.proj_name === pectoLevelLineToShow.toString()}
          currentValues={currentValues}
          setCurrentValues={setCurrentValues}
        />
      )}
    </div>
  );

  if (!cropActive) return projection;

  return (
    <div
      className={classes.cropViewport}
      style={{
        aspectRatio: `${breastBox.w * imgSize[0]} / ${breastBox.h * imgSize[1]}`,
        height: maxProjHeight,
        maxHeight: maxProjHeight,
      }}>
      <div
        className={classes.cropInner}
        style={{
          width: `${100 / breastBox.w}%`,
          height: `${100 / breastBox.h}%`,
          left: `${(-breastBox.x / breastBox.w) * 100}%`,
          top: `${(-breastBox.y / breastBox.h) * 100}%`,
        }}>
        {projection}
      </div>
    </div>
  );
}
